import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  getFieldsByCategoryForView,
  getOptionalFieldsForView,
  isFieldMandatory,
  getFieldWithLabel,
  isBuiltInFieldVisible,
  toUnifiedVisibility,
  toInternalVisibility,
  FIELD_CATEGORIES,
  CATEGORY_ORDER,
} from '../config/fieldVisibility';
import { 
  calculateDropdownPosition, 
  closeAllDropdowns, 
  CLOSE_ALL_DROPDOWNS_EVENT, 
  FLOWCHART_PAN_START_EVENT 
} from '../shared/utils/dropdownPositioning';

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

// Shared icon for all views - card with fields
const FieldsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="7" y1="9" x2="17" y2="9" />
    <line x1="7" y1="13" x2="13" y2="13" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// VIEW CONFIGURATION
// ═══════════════════════════════════════════════════════════════

const VIEW_CONFIG = {
  table: {
    buttonLabel: 'Show/Hide Columns',
    buttonTitle: 'Show/hide columns',
    headerLabel: 'Show Columns',
    icon: FieldsIcon,
  },
  flow: {
    buttonLabel: 'Show/Hide Fields',
    buttonTitle: 'Configure card fields',
    headerLabel: 'Show on Cards',
    icon: FieldsIcon,
  },
  kanban: {
    buttonLabel: 'Show/Hide Fields',
    buttonTitle: 'Configure card fields',
    headerLabel: 'Show on Cards',
    icon: FieldsIcon,
  },
  neural: {
    buttonLabel: 'Show/Hide Fields',
    buttonTitle: 'Configure card fields',
    headerLabel: 'Show on Cards',
    icon: FieldsIcon,
  },
  todolist: {
    buttonLabel: 'Show/Hide Fields',
    buttonTitle: 'Show/hide fields',
    headerLabel: 'Show Fields',
    icon: FieldsIcon,
  },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

/**
 * FieldVisibilityDropdown Component
 * 
 * Unified dropdown for toggling field/column visibility across all views.
 * Adapts its appearance and behavior based on the view prop.
 * 
 * @param {string} view - The view type ('table', 'flow', 'kanban', 'todolist', 'neural')
 * @param {Object} visibility - Current visibility settings { fieldId: boolean }
 * @param {Function} onVisibilityChange - Callback when visibility changes
 * @param {Array} customFields - Optional array of custom field definitions to include
 * @param {Function} onManageCustomFields - Callback to open custom fields manager
 */
const FieldVisibilityDropdown = ({
  view,
  visibility = {},
  onVisibilityChange,
  customFields = [],
  onManageCustomFields,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Get view-specific configuration
  const config = VIEW_CONFIG[view] || VIEW_CONFIG.table;
  const IconComponent = config.icon;

  // Convert incoming visibility from internal IDs to unified IDs
  const unifiedVisibility = useMemo(() => {
    return toUnifiedVisibility(visibility, view);
  }, [visibility, view]);

  // Get fields for this view, grouped by category
  const fieldsByCategory = useMemo(() => {
    return getFieldsByCategoryForView(view);
  }, [view]);

  // Get optional fields (mandatory fields are always on, so we don't show them)
  const optionalFields = useMemo(() => getOptionalFieldsForView(view), [view]);

  // Calculate dropdown position with edge detection
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 240));
    }
  };

  // Open dropdown
  const handleDropdownToggle = () => {
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current);
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Listen for global close events
  useEffect(() => {
    const handleCloseAll = () => setIsOpen(false);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Toggle a field's visibility (using unified IDs internally, convert to internal IDs for callback)
  const handleToggle = (fieldId) => {
    if (isFieldMandatory(fieldId, view)) return;

    const fieldDef = optionalFields.find(field => field.id === fieldId);
    const currentValue = unifiedVisibility[fieldId];
    const isCurrentlyVisible = fieldDef
      ? isBuiltInFieldVisible(currentValue, fieldDef, view)
      : currentValue === true;
    const newUnifiedVisibility = {
      ...unifiedVisibility,
      [fieldId]: !isCurrentlyVisible,
    };
    // Convert back to internal IDs before calling the callback
    onVisibilityChange?.(toInternalVisibility(newUnifiedVisibility, view));
  };

  // Show all optional fields
  const handleShowAll = () => {
    const newUnifiedVisibility = { ...unifiedVisibility };
    optionalFields.forEach(field => {
      newUnifiedVisibility[field.id] = true;
    });
    // Include custom fields
    customFields.forEach(field => {
      newUnifiedVisibility[field.id] = true;
    });
    onVisibilityChange?.(toInternalVisibility(newUnifiedVisibility, view));
  };

  // Hide all optional fields
  const handleHideAll = () => {
    const newUnifiedVisibility = { ...unifiedVisibility };
    optionalFields.forEach(field => {
      newUnifiedVisibility[field.id] = false;
    });
    // Include custom fields
    customFields.forEach(field => {
      newUnifiedVisibility[field.id] = false;
    });
    onVisibilityChange?.(toInternalVisibility(newUnifiedVisibility, view));
  };

  // Count visible optional fields
  // Built-in fields: stored value, or the field's default when unset
  // Custom fields: hidden unless explicitly true
  const visibleCount = optionalFields.filter(
    field => isBuiltInFieldVisible(unifiedVisibility[field.id], field, view)
  ).length + customFields.filter(
    field => unifiedVisibility[field.id] === true
  ).length;
  const totalOptional = optionalFields.length + customFields.length;

  // Render a field item (with view-specific label)
  const renderFieldItem = (fieldDef, isCustomField = false) => {
    // For custom fields, use name directly; for built-in fields, get view-specific label
    const field = isCustomField ? fieldDef : getFieldWithLabel(fieldDef, view);
    const label = isCustomField ? fieldDef.name : field.label;
    const fieldId = field.id;
    // Custom fields default to hidden (must be explicitly true)
    // Built-in fields use the stored value, or the field's default when unset
    const isVisible = isCustomField
      ? unifiedVisibility[fieldId] === true
      : isBuiltInFieldVisible(unifiedVisibility[fieldId], fieldDef, view);

    return (
      <button
        key={fieldId}
        className={`column-visibility-dropdown__item ${isVisible ? 'column-visibility-dropdown__item--selected' : ''}`}
        onClick={() => handleToggle(fieldId)}
      >
        <span className="column-visibility-dropdown__item-check">
          {isVisible && <CheckIcon />}
        </span>
        <span className="column-visibility-dropdown__item-label">
          {label}
        </span>
      </button>
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isOpen ? 'toolbar-btn--active' : ''}`}
        onClick={handleDropdownToggle}
        title={config.buttonTitle}
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon">
            <IconComponent />
          </span>
          <span className="toolbar-btn__label">{config.buttonLabel}</span>
        </span>
        <span className={`toolbar-btn__chevron ${isOpen ? 'toolbar-btn__chevron--open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="column-visibility-dropdown__menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          {/* Header with count and All/None buttons */}
          <div className="column-visibility-dropdown__header">
            <span>{config.headerLabel} ({visibleCount}/{totalOptional})</span>
            <div className="column-visibility-dropdown__header-actions">
              <button 
                className="column-visibility-dropdown__action-btn"
                onClick={handleShowAll}
                title="Show all"
              >
                All
              </button>
              <button 
                className="column-visibility-dropdown__action-btn"
                onClick={handleHideAll}
                title="Hide all optional"
              >
                None
              </button>
            </div>
          </div>
          
          {/* Fields grouped by category - same layout for ALL views */}
          {CATEGORY_ORDER.map(categoryId => {
            // Skip 'custom' category here - we render it separately below
            if (categoryId === 'custom') return null;
            
            const categoryFields = fieldsByCategory[categoryId];
            if (!categoryFields) return null;
            
            // Filter out mandatory fields (they're always on, no need to show)
            const optionalCategoryFields = categoryFields.filter(
              field => !isFieldMandatory(field.id, view)
            );
            
            if (optionalCategoryFields.length === 0) return null;

            return (
              <div key={categoryId} className="column-visibility-dropdown__section">
                <div className="column-visibility-dropdown__section-title">
                  {FIELD_CATEGORIES[categoryId]}
                </div>
                {optionalCategoryFields.map(field => renderFieldItem(field))}
              </div>
            );
          })}

          {/* Custom fields section - individual fields from campaign + add button */}
          <div className="column-visibility-dropdown__section">
            <div className="column-visibility-dropdown__section-title">
              {FIELD_CATEGORIES.custom}
            </div>
            {customFields.map(field => renderFieldItem(field, true))}
            <button
              className="column-visibility-dropdown__add-custom-btn"
              onClick={(e) => {
                e.stopPropagation();
                onManageCustomFields?.();
                setIsOpen(false); // Close the dropdown when opening custom fields manager
              }}
            >
              <span className="column-visibility-dropdown__add-custom-icon">+</span>
              <span>{customFields.length > 0 ? 'Manage Custom Fields' : 'Add Custom Field'}</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FieldVisibilityDropdown;
